import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { ExternalLink } from 'core/routing';
import { html } from 'libs/editor/serializers/html';
import { EditorStore } from 'libs/editor/store';
import { Editor } from 'libs/editor';
import { TextField } from 'libs/forms/views/fieldTypes/text';
import { EditorField } from 'libs/forms/views/fieldTypes/editor';

import {
  SubPageWrapper,
  SubPageTitle,
  SubPageInfo,
  SubPageDescription,
  Label,
  Divider,
  Div,
  BaseEditField,
} from 'legacyComponents/styled/lib';
import { Username } from 'apps/components/shared';
import { media } from 'legacyComponents/styled/styleUtils';
import { Date } from 'apps/components/date';

import { modalStore } from 'apps/modals/app';
import { ConfirmAction } from 'apps/modals/confirmAction/index';

import { Comments } from 'apps/comments/main/index';
import { withNamespaces, Trans } from 'react-i18next';

export const TitleInput = styled(BaseEditField)`
  font-size: 1.5em;
  margin: -4px 0px 10px -2px;
  padding: 4px 2px;
  > input {
    font-weight: 600;
    width: 95%;
  }
  ::placeholder {
    font-weight: 600;
  }
  ${media.s`
    font-size: 1.2em;
  `};
`;

export const MsgInput = styled(BaseEditField)`
  padding: 4px 2px 10px 2px;
  min-height: 100px;
`;

const ExtLink = styled.a`
  &:hover {
    > h2 {
      color: ${props => props.theme.color.primary};
    }
  }
`;

const Origin = styled.span`
  font-size: 0.75em;
  color: ${props => props.theme.color.wfGrey500};
`;

export const Edit = styled.span`
  color: ${props => props.theme.color.wfGrey};
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: ${props => props.theme.color.primary};
  }
`;

export const DeleteBtn = styled(Edit)`
  &:hover {
    color: ${props => props.theme.color.wfOrange};
  }
`;

@withNamespaces()
@observer
export class PostPageContent extends React.Component {
  componentWillUnmount = () => {
    const { post } = this.props;
    if (post.editing) {
      post.Cancel();
    }
  };

  render() {
    const { post } = this.props;
    const {
      title,
      upvoteCount,
      msg,
      url,
      creatorProfile,
      origin,
      isQuestion,
      dateCreated,
      canUpdate,
      editing,
      canDelete,
      Delete,
      EditPost,
      Cancel,
      Save,
    } = post;

    const { username } = creatorProfile;

    const Info = (
      <div>
        <Label>
          <Trans count={upvoteCount}>{{ upvoteCount }} Upvote</Trans>
        </Label>
        <SubPageInfo>
          {isQuestion ? (
            <Trans>
              asked by{' '}
              <Username name='profile' params={{ profile: username }}>
                {{ username }}
              </Username>
            </Trans>
          ) : (
            <Trans>
              posted by{' '}
              <Username name='profile' params={{ profile: username }}>
                {{ username }}
              </Username>
            </Trans>
          )}
          {' | '}
          <Date value={dateCreated} />
          {canUpdate ? (
            editing ? (
              <span>
                {' | '}
                <DeleteBtn onClick={() => Cancel()}>
                  <Trans>Cancel</Trans>
                </DeleteBtn>
                {' | '}
                <Edit onClick={() => Save()}>
                  <Trans>Save</Trans>
                </Edit>
              </span>
            ) : (
              <span>
                <span>
                  {' | '}
                  <Edit onClick={() => EditPost()}>
                    <Trans>Edit</Trans>
                  </Edit>
                </span>
              </span>
            )
          ) : (
            ''
          )}
          {canDelete && !editing ? (
            <span>
              {' | '}
              <DeleteBtn
                onClick={() =>
                  modalStore.SetModalComponent(ConfirmAction, {
                    callBack: Delete,
                    title: 'delete this Post?',
                    undo: false,
                  })
                }
              >
                <Trans>Delete</Trans>
              </DeleteBtn>
            </span>
          ) : (
            ''
          )}
        </SubPageInfo>
      </div>
    );

    if (editing) {
      return (
        <SubPageWrapper>
          <Div position='relative'>
            <TextField
              fieldStore={post.editFormStore.fieldStores.get('title')}
              editComponent={TitleInput}
              validationPosition={{ top: '10px', right: '5px' }}
            />
          </Div>
          <SubPageTitle noLineHeight>
            {origin ? <Origin> ({origin})</Origin> : ''}
          </SubPageTitle>
          {Info}
          <SubPageDescription>
            <Div position='relative'>
              <EditorField
                fieldStore={post.editFormStore.fieldStores.get('msg')}
                editComponent={MsgInput}
                paragraphSpacing='paragraphSpacing12'
                validationPosition={{ top: '10px', right: '5px' }}
                blueBorder
                noFormattingHint
              />
            </Div>
            <Divider width='100%' margin='20px auto 10px auto' />
          </SubPageDescription>
          <Comments content={post} contentType='post' />
        </SubPageWrapper>
      );
    }
    const store = new EditorStore({
      initialValue: html.deserialize(msg || '<p></p>'),
      formatting: 'basic',
    });
    return (
      <SubPageWrapper>
        {url ? (
          <ExternalLink to={url} Styled={ExtLink}>
            <SubPageTitle link>
              <span>{title}</span>
              {origin ? <Origin> ({origin})</Origin> : ''}
            </SubPageTitle>
          </ExternalLink>
        ) : (
          <SubPageTitle>{title}</SubPageTitle>
        )}
        {Info}
        {msg === '<p></p>' || msg === '' ? (
          <SubPageDescription empty>
            <i>
              <Trans>No message provided</Trans>.
            </i>
          </SubPageDescription>
        ) : (
          <SubPageDescription>
            <Editor store={store} paragraphSpacing='paragraphSpacing12' />
            <Divider width='100%' margin='20px auto 10px auto' />
          </SubPageDescription>
        )}
        <Comments content={post} contentType='post' />
      </SubPageWrapper>
    );
  }
}

PostPageContent.propTypes = {
  post: PropTypes.object,
  t: PropTypes.func,
};
